import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    ArrowUpRight,
    Phone,
    Sparkles,
    Check,
} from "lucide-react";

import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import MobileStickyCTA from "@/components/MobileStickyCTA";
import CateringForm from "@/components/sections/CateringForm";

import { BUSINESS } from "@/lib/business-info";
import {
    EXPERIENCES,
    PROCESS,
    GALLERY,
    STATS,
    INCLUDES,
} from "@/lib/private-events-data";

const HERO_BG =
    "https://customer-assets.emergentagent.com/job_caribbean-premium/artifacts/268snciq_4B00982C-BDB8-4044-BB1B-7B7FE2243CA8.PNG";

const SIGNATURE_IMG =
    "https://customer-assets.emergentagent.com/job_b8a7810c-24f2-4266-91d4-0b33b2eb0b66/artifacts/bwzczki2_6AED80A2-E5AD-4A8F-A910-849ACAE56061%202.PNG";

const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

// ---------------- Hero ----------------
function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
    const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.5]);

    return (
        <section
            ref={ref}
            data-testid="private-events-hero"
            className="relative min-h-[92svh] md:min-h-[100svh] overflow-hidden bg-charcoal"
            aria-label="Private events hero"
        >
            <motion.div
                style={{ y }}
                aria-hidden
                className="absolute inset-0 z-0 will-change-transform"
            >
                <div
                    className="absolute inset-0 bg-cover bg-no-repeat"
                    style={{
                        backgroundImage: `url(${HERO_BG})`,
                        backgroundPosition: "center 45%",
                        filter: "brightness(0.9)",
                    }}
                />
                {/* Warm intimate vignette */}
                <div className="absolute inset-0 bg-charcoal/70" />
                <div className="absolute inset-0 bg-gradient-to-r from-charcoal/95 via-charcoal/55 to-charcoal/15" />
                <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-transparent to-charcoal" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_28%_55%,rgba(212,175,55,0.13),rgba(0,0,0,0)_55%)]" />
            </motion.div>

            <motion.div
                style={{ opacity }}
                className="relative z-10 empire-container min-h-[92svh] md:min-h-[100svh] flex flex-col justify-center pt-36 pb-24"
            >
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="empire-eyebrow"
                >
                    <span className="inline-block h-px w-8 bg-gold align-middle mr-3" />
                    Private Dining · {BUSINESS.location}
                </motion.p>

                <motion.h1
                    initial={{ opacity: 0, y: 22 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                        duration: 1,
                        delay: 0.15,
                        ease: [0.22, 1, 0.36, 1],
                    }}
                    data-testid="private-events-headline"
                    className="mt-8 max-w-5xl font-serif text-cream text-[2.6rem] leading-[1.04] tracking-[-0.01em] md:text-[4.4rem] lg:text-[5.6rem]"
                >
                    Private events
                    <br className="hidden md:block" /> designed{" "}
                    <em className="italic font-normal text-gold">with soul.</em>
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.32 }}
                    className="mt-8 max-w-xl text-cream/82 text-[15px] md:text-[17px] font-light leading-[1.75]"
                >
                    From intimate birthdays and anniversaries to elevated private
                    dinners and milestone celebrations — unforgettable gatherings
                    rooted in Caribbean flavor and quiet hospitality.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4"
                >
                    <button
                        type="button"
                        onClick={() => scrollTo("private-events-inquiry")}
                        data-testid="private-events-cta-reserve"
                        className="empire-btn-primary"
                    >
                        Reserve Private Event
                        <ArrowRight size={14} />
                    </button>
                    <a
                        href={BUSINESS.phoneLink}
                        data-testid="private-events-cta-call"
                        className="empire-btn-secondary"
                    >
                        <Phone size={14} />
                        Speak With Our Team
                    </a>
                </motion.div>

                <motion.ul
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.3, delay: 0.9 }}
                    className="hidden md:flex items-center gap-10 mt-20 text-cream/55 text-[11px] uppercase tracking-[0.28em]"
                >
                    <li className="flex items-center gap-3">
                        <span className="h-px w-8 bg-cream/30" />
                        Birthdays
                    </li>
                    <li><span className="text-gold">·</span> Anniversaries</li>
                    <li><span className="text-gold">·</span> Private Dinners</li>
                    <li><span className="text-gold">·</span> Milestone Nights</li>
                </motion.ul>
            </motion.div>

            <div
                aria-hidden
                className="absolute z-10 bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-cream/40 text-[10px] uppercase tracking-[0.32em]"
            >
                <span>Begin</span>
                <span className="block h-10 w-px bg-gradient-to-b from-gold/70 to-transparent" />
            </div>
        </section>
    );
}

// ---------------- Experiences ----------------
function Experiences() {
    return (
        <section
            id="experiences"
            data-testid="experiences-section"
            className="relative empire-section bg-charcoal"
            aria-label="Private event experiences"
        >
            <div className="empire-container">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end mb-14 md:mb-20">
                    <div className="md:col-span-7">
                        <div className="flex items-center gap-3">
                            <span className="h-px w-8 bg-gold" />
                            <span className="empire-eyebrow !text-gold !text-[10px]">
                                Designed Around You
                            </span>
                        </div>
                        <h2 className="mt-5 font-serif text-cream text-[2.4rem] md:text-[3.4rem] lg:text-[4rem] leading-[1.05] tracking-[-0.01em]">
                            Four kinds of evening,<br />
                            <span className="italic text-gold">held with the same care.</span>
                        </h2>
                    </div>
                    <p className="md:col-span-5 empire-body md:text-right max-w-md md:ml-auto">
                        Every private event is a quiet collaboration — we shape the
                        room, the menu, and the pace around the people you&rsquo;re
                        gathering for.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-cream/[0.07]">
                    {EXPERIENCES.map((ex, i) => (
                        <motion.article
                            key={ex.id}
                            initial={{ opacity: 0, y: 22 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{
                                duration: 0.8,
                                delay: i * 0.08,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            data-testid={`experience-card-${ex.id}`}
                            className="group relative overflow-hidden bg-charcoal-800 aspect-[5/6] md:aspect-[6/7]"
                        >
                            <img
                                src={ex.image}
                                alt={`${ex.title} at Empire Jamaica Fusion`}
                                loading="lazy"
                                decoding="async"
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.05]"
                            />
                            <div
                                className={`absolute inset-0 bg-gradient-to-t ${ex.accent} via-charcoal/55 to-transparent`}
                            />
                            <div className="absolute inset-0 bg-charcoal/40 group-hover:bg-charcoal/20 transition-colors duration-500" />

                            <div className="absolute top-7 left-7 right-7 flex items-center justify-between">
                                <span className="text-[10px] uppercase tracking-[0.28em] text-gold">
                                    {ex.guestRange} guests
                                </span>
                                <span className="inline-flex h-9 w-9 items-center justify-center border border-cream/25 text-cream/85 transition-colors group-hover:border-gold group-hover:text-gold">
                                    <ArrowUpRight size={14} />
                                </span>
                            </div>

                            <div className="absolute bottom-0 left-0 right-0 p-7 md:p-9">
                                <h3 className="font-serif text-cream text-[1.8rem] md:text-[2.2rem] tracking-tight leading-[1.05]">
                                    {ex.title}
                                </h3>
                                <p className="mt-3 text-cream/75 text-[13.5px] md:text-[15px] font-light leading-[1.7] max-w-md">
                                    {ex.note}
                                </p>
                            </div>
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ---------------- Signature Private Dining (split) ----------------
function Signature() {
    return (
        <section
            id="signature-dining"
            data-testid="signature-dining"
            className="relative bg-charcoal-800"
            aria-label="Signature private dining experience"
        >
            <div className="empire-container empire-section">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    {/* Image */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-6 relative order-2 lg:order-1"
                    >
                        <div className="relative aspect-[4/5] overflow-hidden">
                            <img
                                src={SIGNATURE_IMG}
                                alt="Signature Caribbean dish plated for a private dining course"
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-charcoal/25" />
                            <div className="absolute -inset-px border border-gold/15 pointer-events-none" />
                        </div>
                        <div className="hidden md:flex absolute -bottom-10 -right-10 bg-charcoal border border-cream/10 px-7 py-6 flex-col gap-1 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)]">
                            <span className="empire-eyebrow !text-gold !text-[10px]">
                                Tasting Menu
                            </span>
                            <span className="font-serif text-cream text-3xl tracking-tight">
                                3 – 6 courses
                            </span>
                            <span className="text-cream/55 text-[12px] font-light">
                                Curated by our chef
                            </span>
                        </div>
                    </motion.div>

                    {/* Text */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{
                            duration: 0.9,
                            delay: 0.15,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                        className="lg:col-span-6 order-1 lg:order-2"
                    >
                        <div className="flex items-center gap-3">
                            <span className="h-px w-8 bg-gold" />
                            <span className="empire-eyebrow !text-gold !text-[10px]">
                                The Empire Evening
                            </span>
                        </div>
                        <h2 className="mt-5 font-serif text-cream text-[2.2rem] md:text-[3rem] lg:text-[3.6rem] leading-[1.05] tracking-[-0.01em]">
                            Signature private<br />
                            <span className="italic text-gold">dining experience.</span>
                        </h2>
                        <p className="mt-7 empire-body max-w-xl">
                            A tasting menu rooted in Caribbean tradition and elevated
                            through patient plating. Warm lighting. Quiet music. A team
                            that knows when to step forward and when to step back.
                            We&rsquo;re here to make the room feel like it was built
                            for your night.
                        </p>

                        {/* Hairline divider */}
                        <div className="my-10 flex items-center gap-4">
                            <span className="h-px w-12 bg-gold/60" />
                            <span className="empire-eyebrow !text-cream/55">
                                Each evening includes
                            </span>
                            <span className="flex-1 h-px bg-cream/[0.08]" />
                        </div>

                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3.5">
                            {INCLUDES.map((item) => (
                                <li
                                    key={item}
                                    className="flex items-start gap-3 text-cream/80 text-[14px] font-light leading-relaxed"
                                    data-testid={`signature-include-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                >
                                    <span className="mt-1 inline-flex h-4 w-4 items-center justify-center border border-gold/60 shrink-0">
                                        <Check size={10} className="text-gold" />
                                    </span>
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

// ---------------- 3-Step Process ----------------
function ProcessSection() {
    return (
        <section
            data-testid="process-section"
            className="relative bg-charcoal border-y border-cream/[0.08]"
            aria-label="The Empire evening flow"
        >
            <div className="empire-container py-20 md:py-28">
                <div className="text-center max-w-2xl mx-auto mb-14 md:mb-20">
                    <div className="inline-flex items-center gap-3">
                        <span className="h-px w-8 bg-gold" />
                        <span className="empire-eyebrow !text-gold !text-[10px]">
                            How An Evening Begins
                        </span>
                        <span className="h-px w-8 bg-gold" />
                    </div>
                    <h2 className="mt-5 font-serif text-cream text-[2.2rem] md:text-[3rem] leading-[1.05] tracking-[-0.01em]">
                        Three quiet steps,<br />
                        <span className="italic text-gold">one unforgettable night.</span>
                    </h2>
                </div>

                <ol className="relative grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-0">
                    {/* horizontal hairline behind numerals on md+ */}
                    <span
                        aria-hidden
                        className="hidden md:block absolute top-[34px] left-[12%] right-[12%] h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent"
                    />
                    {PROCESS.map((p, i) => (
                        <motion.li
                            key={p.step}
                            initial={{ opacity: 0, y: 22 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{
                                duration: 0.8,
                                delay: i * 0.12,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            data-testid={`process-step-${i + 1}`}
                            className="relative md:px-8 text-center md:text-left"
                        >
                            <div className="font-serif italic text-gold/85 text-[3.4rem] md:text-[4rem] leading-none tracking-tight">
                                {p.step}
                            </div>
                            <h3 className="mt-6 font-serif text-cream text-[1.5rem] md:text-[1.7rem] leading-tight tracking-tight">
                                {p.title}
                            </h3>
                            <p className="mt-4 text-cream/65 text-[14px] md:text-[15px] font-light leading-[1.75] max-w-sm md:max-w-xs mx-auto md:mx-0">
                                {p.note}
                            </p>
                        </motion.li>
                    ))}
                </ol>
            </div>
        </section>
    );
}

// ---------------- Gallery ----------------
function GallerySection() {
    return (
        <section
            id="gallery-preview"
            data-testid="gallery-preview"
            className="relative empire-section bg-charcoal-800"
            aria-label="Gallery preview"
        >
            <div className="empire-container">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                    <div>
                        <div className="flex items-center gap-3">
                            <span className="h-px w-8 bg-gold" />
                            <span className="empire-eyebrow !text-gold !text-[10px]">
                                Moments
                            </span>
                        </div>
                        <h2 className="mt-5 font-serif text-cream text-[2.2rem] md:text-[3rem] leading-[1.05] tracking-[-0.01em]">
                            A look into<br />
                            <span className="italic text-gold">our private nights.</span>
                        </h2>
                    </div>
                    <Link
                        to="/gallery"
                        data-testid="gallery-view-full"
                        className="empire-btn-secondary"
                    >
                        View Full Gallery
                        <ArrowUpRight size={14} />
                    </Link>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-12 gap-2 md:gap-3">
                    {GALLERY.map((img, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 22 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{
                                duration: 0.8,
                                delay: i * 0.08,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            data-testid={`gallery-tile-${i}`}
                            className={`group relative overflow-hidden ${img.span}`}
                        >
                            <img
                                src={img.src}
                                alt={img.alt}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-[1.07]"
                            />
                            <div className="absolute inset-0 bg-charcoal/25 group-hover:bg-charcoal/0 transition-colors duration-500" />
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ---------------- Trust block ----------------
function TrustBlock() {
    return (
        <section
            data-testid="pe-trust"
            className="relative bg-charcoal"
            aria-label="Trust"
        >
            <div className="empire-container py-20 md:py-28">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-end mb-12 md:mb-16">
                    <div className="md:col-span-7">
                        <div className="flex items-center gap-3">
                            <span className="h-px w-8 bg-gold" />
                            <span className="empire-eyebrow !text-gold !text-[10px]">
                                Quietly Trusted
                            </span>
                        </div>
                        <h2 className="mt-5 font-serif text-cream text-[2.2rem] md:text-[3rem] leading-[1.05] tracking-[-0.01em]">
                            A reputation built<br />
                            <span className="italic text-gold">one evening at a time.</span>
                        </h2>
                    </div>
                    <p className="md:col-span-5 empire-body md:text-right max-w-md md:ml-auto">
                        We&rsquo;ve hosted hundreds of intimate gatherings across the
                        Hudson Valley — and treated every one like the only one on
                        the calendar.
                    </p>
                </div>

                <ul className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-cream/[0.07]">
                    {STATS.map((s, i) => (
                        <motion.li
                            key={s.label}
                            initial={{ opacity: 0, y: 22 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{
                                duration: 0.8,
                                delay: i * 0.1,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            data-testid={`pe-stat-${i}`}
                            className="bg-charcoal-800 p-8 md:p-10 flex flex-col gap-3 hover:bg-charcoal-700 transition-colors duration-500"
                        >
                            <div className="font-serif text-gold text-[2.6rem] md:text-[3.2rem] leading-none tracking-tight">
                                {s.value}
                            </div>
                            <div className="font-serif text-cream text-[1.05rem] md:text-[1.2rem] leading-tight tracking-tight">
                                {s.label}
                            </div>
                            <p className="text-cream/50 text-[12.5px] font-light tracking-wide">
                                {s.note}
                            </p>
                        </motion.li>
                    ))}
                </ul>
            </div>
        </section>
    );
}

// ---------------- Inquiry wrapper that auto-selects Private Event ----------------
function InquiryWrapper() {
    useEffect(() => {
        // Defer one tick so CateringForm's mount-time event listener is ready,
        // then dispatch the type switch.
        const t = setTimeout(() => {
            window.dispatchEvent(
                new CustomEvent("set-inquiry-type", { detail: "private_event" })
            );
        }, 60);
        return () => clearTimeout(t);
    }, []);
    return (
        <div id="private-events-inquiry" data-testid="private-events-inquiry">
            <CateringForm />
        </div>
    );
}

// ---------------- Emotional Final CTA (page-specific) ----------------
function PrivateFinalCTA() {
    return (
        <section
            data-testid="pe-final-cta"
            className="relative overflow-hidden bg-charcoal"
            aria-label="Reserve your date"
        >
            <div
                aria-hidden
                className="absolute inset-0 z-0"
            >
                <img
                    src="https://customer-assets.emergentagent.com/job_b8a7810c-24f2-4266-91d4-0b33b2eb0b66/artifacts/cgxmtqev_90DA897D-570F-4536-9634-1812C7F856DA.PNG"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-35"
                />
                <div className="absolute inset-0 bg-burgundy/40" />
                <div className="absolute inset-0 bg-gradient-to-b from-charcoal/85 via-burgundy/60 to-charcoal" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.10),rgba(0,0,0,0)_55%)]" />
            </div>

            <div className="relative z-10 empire-container py-28 md:py-40 text-center">
                <Sparkles size={22} strokeWidth={1.4} className="text-gold mx-auto" />
                <motion.h2
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                        duration: 0.9,
                        delay: 0.1,
                        ease: [0.22, 1, 0.36, 1],
                    }}
                    data-testid="pe-final-cta-heading"
                    className="mt-8 mx-auto max-w-4xl font-serif text-cream text-[2.4rem] md:text-[4rem] lg:text-[4.6rem] leading-[1.05] tracking-[-0.01em]"
                >
                    Let&rsquo;s create<br />
                    <span className="italic text-gold">something unforgettable.</span>
                </motion.h2>
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.25 }}
                    className="mt-8 max-w-xl mx-auto text-cream/80 text-[15px] md:text-[17px] font-light leading-[1.75]"
                >
                    Whether it&rsquo;s an intimate dinner or a milestone celebration,
                    we&rsquo;ll help bring your gathering to life with elevated
                    Caribbean hospitality.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.4 }}
                    className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
                >
                    <button
                        type="button"
                        onClick={() => scrollTo("private-events-inquiry")}
                        data-testid="pe-final-cta-reserve"
                        className="inline-flex items-center justify-center gap-2 bg-cream text-charcoal px-8 py-4 text-[12px] uppercase tracking-[0.22em] font-medium hover:bg-gold transition-colors duration-300"
                    >
                        Reserve Your Date
                        <ArrowRight size={14} />
                    </button>
                    <a
                        href={BUSINESS.phoneLink}
                        data-testid="pe-final-cta-call"
                        className="inline-flex items-center justify-center gap-2 border border-cream/60 text-cream px-8 py-4 text-[12px] uppercase tracking-[0.22em] font-medium hover:bg-cream hover:text-charcoal transition-colors duration-300"
                    >
                        {BUSINESS.phoneDisplay}
                    </a>
                </motion.div>
            </div>
        </section>
    );
}

// ---------------- Page ----------------
export default function PrivateEvents() {
    useEffect(() => {
        document.title = "Private Events · Empire Jamaica Fusion · Poughkeepsie, NY";
        window.scrollTo({ top: 0, behavior: "auto" });
    }, []);

    return (
        <>
            <Nav />
            <main data-testid="private-events-page" className="overflow-x-hidden">
                <Hero />
                <Experiences />
                <Signature />
                <ProcessSection />
                <GallerySection />
                <TrustBlock />
                <InquiryWrapper />
                <PrivateFinalCTA />
            </main>
            <Footer />
            <MobileStickyCTA />
        </>
    );
}
